import { Modal } from "flowbite-react";
import { FormikProvider, useFormik } from "formik";
import InputFormik from "../../../../common/components/form/InputFormik";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { differenceInDays } from "date-fns";

import "./ModalProRata.scss";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import Loading from "../../../../common/components/ui/loading/Loading";
import { PaymentInfo } from "@/types/payment.type";
import { updateAsassPayment } from "@/common/services/payments.api";
import { formatDate } from "@/common/helpers/date.helpers";

type Props = {
  onClosed: () => void;
  open: boolean
  payments: PaymentInfo[];
}

export function ModalProRata(props: Props) {

  const validationSchema = Yup.object().shape({
    nextDueDate: Yup.string().required('Data da primeira parcela é obrigatório').test('nextDueDate',
      'A data não pode ser igual ou anterior a data de vencimento da próxima fatura.', (value) => getProRataValue(value) === '' ? false : true)
  });

  const handleCloseModal = () => { props.onClosed && props.onClosed() };
  const { renterId } = useParams()
  const [lastPayment, setLastPayment] = useState<PaymentInfo>();
  const [countDayChange, setCountDayChange] = useState(0);
  const { notifySuccess, notifyError } = useToastContext();

  const safeDateParse = (dateString: string | undefined): number => {
    if (!dateString) return Date.now(); // Retorna agora se indefinido
    return new Date(dateString).getTime();
  };

  useEffect(() => {
    if (!props.open || !renterId || props.payments.length === 0) {
      return;
    }

    formik.values.nextDueDate = "";

    const now = Date.now();

    // Primeiro, procura por pagamentos vencidos (que precisam de Pro Rata)
    const overduePayments = props.payments.filter(payment =>
      payment.dueDate && safeDateParse(payment.dueDate) < now
    );

    let selectedPayment;

    if (overduePayments.length > 0) {
      // Se há pagamentos vencidos, pega o mais recente (menos vencido)
      selectedPayment = overduePayments.reduce((prev, curr) => {
        const prevDate = safeDateParse(prev.dueDate);
        const currDate = safeDateParse(curr.dueDate);
        return currDate > prevDate ? curr : prev; // O mais recente entre os vencidos
      });
    } else {
      // Fallback: se não há vencidos, pega o próximo pagamento futuro
      const futurePayments = props.payments.filter(payment =>
        payment.dueDate && safeDateParse(payment.dueDate) > now
      );

      if (futurePayments.length > 0) {
        selectedPayment = futurePayments.reduce((prev, curr) => {
          const prevDate = safeDateParse(prev.dueDate);
          const currDate = safeDateParse(curr.dueDate);
          return currDate < prevDate ? curr : prev; // O mais próximo entre os futuros
        });
      }
    }

    setLastPayment(selectedPayment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renterId, props.open]);

  const formik = useFormik({
    initialValues: {
      nextDueDate: "",
      renterId,
    },

    onSubmit: async (values) => {

      try {
        if (!values.renterId || !values.nextDueDate || !lastPayment) {
          return;
        }

        const nextDueDate = values.nextDueDate as unknown as Date;
        const data = {
          renterId: values.renterId,
          dueDate: nextDueDate,
          newValueFirstWeek: Number(getProRataValue()) + lastPayment.value,
          countDayChange: countDayChange
        }
        const response = await updateAsassPayment(data);
        if (!response.ok && response.message) {
          notifyError(response.message);
          return;
        }

        handleCloseModal();
        notifySuccess("Pro Rata realizado com sucesso");
      } catch (e) {
        console.error(e);
      }
    },
    validationSchema
  });

  // Função para calcular apenas o valor, sem side effects
  function calculateProRataValue(value?: string): string {
    const inputValue = value || formik.values.nextDueDate
    if (!lastPayment?.value) {
      return "";
    }

    const days = differenceInDays(inputValue, new Date(lastPayment?.dueDate as string));
    const calc = ((lastPayment?.value / 7) * days).toFixed(2)

    return Number(calc) > 0 ? calc : "";
  }

  // Função para calcular os dias
  function calculateDayCount(value?: string): number {
    const inputValue = value || formik.values.nextDueDate
    if (!lastPayment?.value) {
      return 0;
    }

    const proRataValue = Number(calculateProRataValue(inputValue));
    return Math.round(proRataValue / (lastPayment.value / 7));
  }

  // useEffect para atualizar o estado quando necessário
  useEffect(() => {
    if (formik.values.nextDueDate && lastPayment?.value) {
      const dayCount = calculateDayCount();
      setCountDayChange(dayCount);
    } else {
      setCountDayChange(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.nextDueDate, lastPayment]);

  // Mantenha a função original apenas para validação do Yup
  function getProRataValue(value?: string) {
    return calculateProRataValue(value);
  }

  return (
    <Modal
      show={props.open}
      onClose={handleCloseModal}
    >
      <Modal.Header className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-center">
          <h2 className="text-xl font-bold text-gray-800">PRO Rata</h2>
        </div>
      </Modal.Header>

      {lastPayment ? (
        <Modal.Body className="space-y-6 p-6">
          <FormikProvider value={formik}>
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-blue-700 font-medium">Data do primeiro pagamento:</span>
                <span className="text-blue-900 font-bold">
                  {formatDate(lastPayment.dueDate)}
                </span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-blue-700 font-medium">Valor semanal atual:</span>
                <span className="text-blue-900 font-bold">
                  R$ {lastPayment.value.toFixed(2).replace(".", ",")}
                </span>
              </div>
            </div>
            {/* Seção de Data */}
            <div className="space-y-2">
              <InputFormik
                label="Data de cobrança da primeira parcela - Assinatura"
                name="nextDueDate"
                type="date"
                required={true}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Card de Informações */}
            {/* Card de Informações */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Valor da diária:</span>
                <span className="text-gray-800 font-semibold">
                  R${(lastPayment.value / 7).toFixed(2).replace(".", ",")}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Quantidade de diárias:</span>
                <span className="text-gray-800 font-semibold">
                  {countDayChange}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Valor do Pro Rata:</span>
                <span className="text-blue-600 font-bold">
                  R${calculateProRataValue().replace(".", ",")}
                </span>
              </div>

              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-gray-700 font-semibold">Novo valor semanal:</span>
                <span className="text-green-600 font-bold">
                  R${(Number(calculateProRataValue()) + lastPayment.value).toFixed(2).replace(".", ",")}
                </span>
              </div>
            </div>

            {/* Botão de Envio */}
            <div className="pt-4 flex justify-center">
              <ButtonWithLoader
                disabled={Object.keys(formik.errors).length > 0}
                type="submit"
                isLoading={formik.isSubmitting}
                onClick={formik.handleSubmit}
              >
                Enviar
              </ButtonWithLoader>
            </div>
          </FormikProvider>
        </Modal.Body>
      ) : (
        <Modal.Body className="flex items-center justify-center p-8">
          <Loading />
        </Modal.Body>
      )}
    </Modal>
  )
}